import ls from "services/ls";
import { fetchIpCountry, normalizeCountryCode } from "utils/registrationStates";

/**
 * Resolves the country to send to the API as the `X-Request-Country` header,
 * which drives per-country content variants on the backend (GCC: en-AU/en-NZ/
 * en-CA/en-IE). Resolution order: manual selector override -> logged-in account
 * country -> cached geo-IP.
 *
 * IMPORTANT: getRequestCountry() is READ-ONLY (no network) so it is safe to call
 * inside the request-header path without recursing through the API layer. The
 * geo-IP lookup runs once at app startup via resolveGeoCountry().
 */

const MANUAL_KEY = "country";          // { code } — set by the selector (manual override)
const ACCOUNT_KEY = "account_country"; // { code } — the logged-in player's own country
const GEO_KEY = "geo_country";         // { code, ts } — cached IP-resolved country
const GEO_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

// Broadcast so country-aware UI (banners, promotions, the selector's own flag)
// can refetch/re-render in place instead of forcing a full page reload.
export const COUNTRY_CHANGED_EVENT = "gcc:country-changed";
export function notifyCountryChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(COUNTRY_CHANGED_EVENT));
  }
}

// Fired once when the geo-IP lookup first resolves a country. ONLY the header
// flag listens to this. Content components (banners/promotions/feature cards)
// already await resolveGeoCountry before their first fetch, so they deliberately
// do NOT listen here — re-firing a refetch on geo-resolve is what caused the
// banner double-fetch/slide-overlap glitch. This event lets the flag catch up on
// a cold load regardless of which component's resolveGeoCountry call wins the race.
export const GEO_RESOLVED_EVENT = "gcc:geo-resolved";

/**
 * The country code to attach to API requests, or "" when unknown (the backend
 * then falls back to the base language). Never performs a network call.
 */
export function getRequestCountry() {
  const manual = ls.get(MANUAL_KEY);
  if (manual && manual.code) return normalizeCountryCode(manual.code);

  const account = ls.get(ACCOUNT_KEY);
  if (account && account.code) return normalizeCountryCode(account.code);

  const geo = ls.get(GEO_KEY);
  if (geo && geo.code && typeof geo.ts === "number" && Date.now() - geo.ts < GEO_TTL_MS) {
    return normalizeCountryCode(geo.code);
  }
  return "";
}

/**
 * One-time geo-IP resolution, cached to localStorage. Skipped when a manual
 * override is set or a fresh geo result is already cached. Must be called at
 * app startup only — NOT from the request-header path — so it can never recurse
 * through the API. Only successful (non-empty) results are cached, so a failed
 * lookup is retried on the next startup. Failures are swallowed.
 */
export async function resolveGeoCountry(api) {
  const manual = ls.get(MANUAL_KEY);
  if (manual && manual.code) return;

  const geo = ls.get(GEO_KEY);
  if (geo && geo.code && typeof geo.ts === "number" && Date.now() - geo.ts < GEO_TTL_MS) return;

  try {
    const code = normalizeCountryCode(await fetchIpCountry(api));
    if (code) {
      ls.set(GEO_KEY, { code, ts: Date.now() });
      // Header-only notification. Deliberately NOT COUNTRY_CHANGED_EVENT: content
      // components already awaited this resolution before their first fetch, so
      // re-firing a content refetch here would double-fetch and glitch the banner
      // slider. Only the flag listens to GEO_RESOLVED_EVENT.
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event(GEO_RESOLVED_EVENT));
      }
    }
  } catch (_) {
    // ignore — country stays unresolved ("")
  }
}

/**
 * Manual override from the country/language selector. Pass a falsy/empty value
 * to clear it (fall back to geo-IP). The caller is responsible for reloading so
 * cached content is refetched with the new country.
 */
export function setManualCountry(code) {
  const norm = normalizeCountryCode(code);
  ls.set(MANUAL_KEY, norm ? { code: norm } : null);
  notifyCountryChanged();
}

/** The manual override country code, or "" when none is set. */
export function getManualCountry() {
  const manual = ls.get(MANUAL_KEY);
  return manual && manual.code ? normalizeCountryCode(manual.code) : "";
}

/**
 * Account-tier country: the logged-in player's own country (from their profile).
 * Sits below the manual selector override but above geo-IP, so a signed-in
 * player gets content for their real country even on a shared/VPN'd connection,
 * while still being able to override it manually in the selector. No TTL — it is
 * authoritative for the session and is cleared on logout via clearAccountCountry().
 * Pass a falsy/invalid value to clear it.
 */
export function setAccountCountry(code) {
  const norm = normalizeCountryCode(code);
  ls.set(ACCOUNT_KEY, norm ? { code: norm } : null);
}

/** The account-tier country code, or "" when none is set. */
export function getAccountCountry() {
  const account = ls.get(ACCOUNT_KEY);
  return account && account.code ? normalizeCountryCode(account.code) : "";
}

/** Clear the account-tier country. Call on logout (incl. token expiry). */
export function clearAccountCountry() {
  ls.set(ACCOUNT_KEY, null);
}
